import React from "react";
import { Button, Result } from "antd";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to an error reporting service if needed
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Check if the error is due to a failed chunk loading (common in new deployments)
    const isChunkLoadFailed = 
      error.name === "ChunkLoadError" || 
      /chunk/i.test(error.message) || 
      /loading.*failed/i.test(error.message);

    if (isChunkLoadFailed) {
      const lastReloaded = sessionStorage.getItem("last-chunk-reload");
      const now = Date.now();
      
      // Prevent infinite reload loops by only reloading once per 10 seconds
      if (!lastReloaded || now - parseInt(lastReloaded, 10) > 10000) {
        sessionStorage.setItem("last-chunk-reload", now.toString());
        console.warn("Chunk loading failed. Reloading the page to get the latest build...");
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4">
          <div className="w-full max-w-lg p-8 bg-white rounded-2xl shadow-xl border border-gray-100 text-center transform transition-all duration-300 hover:shadow-2xl">
            <Result
              status="500"
              title="Oops! Something went wrong"
              subTitle={
                <div className="mt-2 text-gray-500">
                  <p className="text-base font-medium text-gray-700 mb-2">
                    The application encountered an unexpected error.
                  </p>
                  <div className="p-3 bg-red-50 rounded-lg text-xs font-mono text-red-600 max-h-32 overflow-y-auto text-left border border-red-100">
                    {this.state.error?.toString() || "Unknown Error"}
                  </div>
                </div>
              }
              extra={[
                <Button
                  key="retry"
                  type="primary"
                  size="large"
                  className="bg-blue-600 hover:bg-blue-700 border-none px-6 rounded-lg font-medium shadow-md transition-all duration-200"
                  onClick={this.handleReload}
                >
                  Reload Application
                </Button>
              ]}
            />
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
